import {
  RiCheckLine,
  RiErrorWarningLine,
  RiHistoryLine,
  RiLoader4Line,
} from "react-icons/ri";
import { StatusTagMetadata } from "src/components/DataDisplay/StatusTag";
import type {
  BulkAction,
  BulkActionItem,
} from "src/generated/hooks/form-service/requests/types.gen";

export type BulkActionType = BulkAction["actionType"];

export const ACTION_TYPE_LABELS: Record<BulkActionType, string> = {
  SET_FORM_STATUS: "Update status",
  SET_FORM_VISIBILITY: "Update visibility",
  VERIFY_FORM: "Verify",
  EXECUTE_FORM_RULES: "Execute form rules",
  ADD_TO_WAITLIST: "Add to waitlist",
  REMOVE_FROM_WAITLIST: "Remove from waitlist",
  MAKE_OFFER: "Make offer",
  ACCEPT_OFFER: "Accept offer",
  DECLINE_OFFER: "Decline offer",
  REVOKE_OFFER: "Revoke offer",
  CLEAR_AUTO_REVOKE: "Clear auto revoke",
  EXCLUDE_FROM_OFFERS: "Exclude from offers",
  CLEAR_SUBSTATUS: "Clear substatus",
  ADD_TAG: "Add tag",
  REMOVE_TAG: "Remove tag",
};

export const ACTION_TYPE_OPTIONS = Object.entries(ACTION_TYPE_LABELS).map(
  ([id, label]) => ({ id, label })
);

export const actionTypesMatchingSearch = (search: string): BulkActionType[] => {
  const term = search.trim().toLowerCase();
  if (!term) {
    return [];
  }

  return (
    Object.entries(ACTION_TYPE_LABELS) as [BulkActionType, string][]
  ).flatMap(([id, label]) =>
    label.toLowerCase().includes(term) ||
    id.toLowerCase().replaceAll("_", " ").includes(term)
      ? [id]
      : []
  );
};

const pluralize = (count: number, singular: string, plural: string): string =>
  count === 1 ? singular : plural;

const isOfferActionType = (actionType: BulkActionType): boolean => {
  switch (actionType) {
    case "MAKE_OFFER":
    case "ACCEPT_OFFER":
    case "DECLINE_OFFER":
    case "REVOKE_OFFER":
    case "CLEAR_AUTO_REVOKE":
      return true;
    default:
      return false;
  }
};

export const getBulkActionNoun = (
  actionType: BulkActionType,
  count: number
): string =>
  isOfferActionType(actionType)
    ? pluralize(count, "offer", "offers")
    : pluralize(count, "form", "forms");

export const processingCopy = (
  actionType: BulkActionType,
  count: number
): string => {
  const noun = getBulkActionNoun(actionType, count);
  switch (actionType) {
    case "MAKE_OFFER":
      return `Making ${count} ${noun}...`;
    case "ACCEPT_OFFER":
      return `Accepting ${count} ${noun}...`;
    case "DECLINE_OFFER":
      return `Declining ${count} ${noun}...`;
    case "REVOKE_OFFER":
      return `Revoking ${count} ${noun}...`;
    case "CLEAR_AUTO_REVOKE":
      return `Clearing auto revoke on ${count} ${noun}...`;
    case "ADD_TO_WAITLIST":
      return `Adding ${count} ${noun} to the waitlist...`;
    case "REMOVE_FROM_WAITLIST":
      return `Removing ${count} ${noun} from the waitlist...`;
    case "SET_FORM_STATUS":
      return `Updating status on ${count} ${noun}...`;
    case "SET_FORM_VISIBILITY":
      return `Updating visibility on ${count} ${noun}...`;
    case "VERIFY_FORM":
      return `Verifying ${count} ${noun}...`;
    case "EXECUTE_FORM_RULES":
      return `Executing form rules on ${count} ${noun}...`;
    case "EXCLUDE_FROM_OFFERS":
      return `Excluding ${count} ${noun}...`;
    case "CLEAR_SUBSTATUS":
      return `Clearing substatus on ${count} ${noun}...`;
    case "ADD_TAG":
      return `Adding tag to ${count} ${noun}...`;
    case "REMOVE_TAG":
      return `Removing tag from ${count} ${noun}...`;
  }
};

export const successCopy = (
  actionType: BulkActionType,
  count: number
): string => {
  const noun = getBulkActionNoun(actionType, count);
  switch (actionType) {
    case "MAKE_OFFER":
      return `${count} ${noun} made`;
    case "ACCEPT_OFFER":
      return `${count} ${noun} accepted`;
    case "DECLINE_OFFER":
      return `${count} ${noun} declined`;
    case "REVOKE_OFFER":
      return `${count} ${noun} revoked`;
    case "CLEAR_AUTO_REVOKE":
      return `${count} ${noun} cleared`;
    case "ADD_TO_WAITLIST":
      return `${count} ${noun} added to the waitlist`;
    case "REMOVE_FROM_WAITLIST":
      return `${count} ${noun} removed from the waitlist`;
    case "SET_FORM_STATUS":
      return `${count} ${noun} updated`;
    case "SET_FORM_VISIBILITY":
      return `${count} ${noun} updated`;
    case "VERIFY_FORM":
      return `${count} ${noun} verified`;
    case "EXECUTE_FORM_RULES":
      return `Form rules executed on ${count} ${noun}`;
    case "EXCLUDE_FROM_OFFERS":
      return `${count} ${noun} excluded`;
    case "CLEAR_SUBSTATUS":
      return `${count} ${noun} cleared`;
    case "ADD_TAG":
      return `${count} ${noun} tagged`;
    case "REMOVE_TAG":
      return `${count} ${noun} untagged`;
  }
};

export const partialFailureCopy = (
  actionType: BulkActionType,
  count: number
): string => {
  const noun = getBulkActionNoun(actionType, count);
  switch (actionType) {
    case "MAKE_OFFER":
      return `Some ${noun} weren't made`;
    case "ACCEPT_OFFER":
      return `Some ${noun} weren't accepted`;
    case "DECLINE_OFFER":
      return `Some ${noun} weren't declined`;
    case "REVOKE_OFFER":
      return `Some ${noun} weren't revoked`;
    case "CLEAR_AUTO_REVOKE":
      return `Some ${noun} weren't cleared`;
    case "ADD_TO_WAITLIST":
      return `Some ${noun} weren't added to the waitlist`;
    case "REMOVE_FROM_WAITLIST":
      return `Some ${noun} weren't removed from the waitlist`;
    case "SET_FORM_STATUS":
      return `Some ${noun} weren't updated`;
    case "SET_FORM_VISIBILITY":
      return `Some ${noun} weren't updated`;
    case "VERIFY_FORM":
      return `Some ${noun} weren't verified`;
    case "EXECUTE_FORM_RULES":
      return `Form rules weren't executed on some ${noun}`;
    case "EXCLUDE_FROM_OFFERS":
      return `Some ${noun} weren't excluded`;
    case "CLEAR_SUBSTATUS":
      return `Some ${noun} weren't cleared`;
    case "ADD_TAG":
      return `Some ${noun} weren't tagged`;
    case "REMOVE_TAG":
      return `Some ${noun} weren't untagged`;
  }
};

export const getAppliedToLabel = (activity: {
  actionType: BulkActionType;
  totalCount: number;
}): string => {
  const count = activity.totalCount;
  return `${count} ${getBulkActionNoun(activity.actionType, count)}`;
};

export const getActivityTitle = (activity: {
  actionType: BulkActionType;
  totalCount: number;
}): string => {
  const count = activity.totalCount;
  const noun = getBulkActionNoun(activity.actionType, count);

  switch (activity.actionType) {
    case "MAKE_OFFER":
      return `Make ${count} ${noun}`;
    case "ACCEPT_OFFER":
      return `Accept ${count} ${noun}`;
    case "DECLINE_OFFER":
      return `Decline ${count} ${noun}`;
    case "REVOKE_OFFER":
      return `Revoke ${count} ${noun}`;
    case "CLEAR_AUTO_REVOKE":
      return `Clear auto revoke on ${count} ${noun}`;
    case "EXCLUDE_FROM_OFFERS":
      return `Exclude ${count} ${noun}`;
    case "ADD_TO_WAITLIST":
      return `Add ${count} ${noun} to the waitlist`;
    case "REMOVE_FROM_WAITLIST":
      return `Remove ${count} ${noun} from the waitlist`;
    case "SET_FORM_STATUS":
      return `Update status on ${count} ${noun}`;
    case "SET_FORM_VISIBILITY":
      return `Update visibility on ${count} ${noun}`;
    case "VERIFY_FORM":
      return `Verify ${count} ${noun}`;
    case "EXECUTE_FORM_RULES":
      return `Execute form rules on ${count} ${noun}`;
    case "CLEAR_SUBSTATUS":
      return `Clear substatus on ${count} ${noun}`;
    case "ADD_TAG":
      return `Add tag to ${count} ${noun}`;
    case "REMOVE_TAG":
      return `Remove tag from ${count} ${noun}`;
  }
};

export const ACTIVITY_STATUS_OPTIONS = [
  { id: "CREATED", label: "Queued" },
  { id: "STARTED", label: "Started" },
  { id: "DONE_WITH_ERRORS", label: "Done (with errors)" },
  { id: "DONE", label: "Done" },
];

const doneStatus = (
  label: string,
  colorScheme: StatusTagMetadata["colorScheme"],
  icon: StatusTagMetadata["icon"]
): StatusTagMetadata => ({
  label,
  longLabel: label,
  colorScheme,
  icon,
});

export const getActivityStatusMetadata = (activity: {
  status: BulkAction["status"];
  errorCount: number;
}): StatusTagMetadata => {
  switch (activity.status) {
    case "CREATED":
      return {
        label: "Queued",
        longLabel: "Queued",
        colorScheme: "orange",
        icon: RiHistoryLine,
      };
    case "STARTED":
      return {
        label: "Started",
        longLabel: "Started",
        colorScheme: "blue",
        icon: RiLoader4Line,
      };
    case "DONE": {
      if (activity.errorCount > 0) {
        return doneStatus("Done (with errors)", "red", RiErrorWarningLine);
      }
      return doneStatus("Done", "green", RiCheckLine);
    }
  }
};

export const ITEM_STATUS_OPTIONS = [
  { id: "DONE", label: "Done" },
  { id: "FAILED", label: "Failed" },
  { id: "CREATED", label: "Queued" },
  { id: "STARTED", label: "Started" },
];

export const getItemStatusMetadata = (
  status: BulkActionItem["status"]
): StatusTagMetadata => {
  switch (status) {
    case "DONE":
      return {
        label: "Done",
        longLabel: "Done",
        colorScheme: "green",
        icon: RiCheckLine,
      };
    case "FAILED":
      return {
        label: "Failed",
        longLabel: "Failed",
        colorScheme: "red",
        icon: RiErrorWarningLine,
      };
    case "CREATED":
      return {
        label: "Queued",
        longLabel: "Queued",
        colorScheme: "orange",
        icon: RiHistoryLine,
      };
    case "STARTED":
      return {
        label: "Started",
        longLabel: "Started",
        colorScheme: "blue",
        icon: RiLoader4Line,
      };
  }
};
